import 'react-toastify/dist/ReactToastify.css';
import '@/styles/global.css';

import { GoogleTagManager, sendGTMEvent } from '@next/third-parties/google';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Provider } from 'react-redux';

import KangWidget from '@/components/kang-widget';
import { OpenAiAdsPixel } from '@/components/openai-ads-pixel';
import { PostHogAnalytics } from '@/components/posthog-analytics';
import { CookieConsent } from '@/components/ui/cookie-consent';
import { ToastHost } from '@/components/ui/toast-host';
import YandexMetrica from '@/components/yandex-metrica';
import { SESSION_STORAGE_COUNTRY_CODE } from '@/constants/session-storage';
import { AuthProvider } from '@/context/auth-context';
import { CieloProvider } from '@/context/cielo-context';
import { FeatureToggleProvider } from '@/contexts/feature-toggle-context';
import { TrialSignUpProvider } from '@/contexts/trial-sign-up-context';
import { CountryCodeEnum } from '@/enums/country-code-enum';
import { useClickIdCapture } from '@/hooks/use-click-id-capture';
import { useDocumentLanguage } from '@/hooks/use-document-language';
import { useUtmTracker } from '@/hooks/use-utm-tracker';
import { LazyDashboardLayout } from '@/layouts/lazy-dashboard-layout';
import { bai_jamjuree } from '@/public/assets/fonts/Bai-Jamjuree';
import { store } from '@/store';
import { setCountryCode } from '@/store/country-code-store';
import { consentModeBootstrapScript } from '@/utils/consent';
import { syncHomeCtaAttribute } from '@/utils/home-cta-cookie';
import { runAfterLoadWhenIdle } from '@/utils/run-after-load-when-idle';
import { runWhenIdle } from '@/utils/run-when-idle';
import { isTrackingExemptRoute } from '@/utils/tracking-exempt-route';
import { warmBlogCache } from '@/utils/warm-blog-cache';

// A Home nova (4 variantes): a / (com o rodízio, SCRUM-2432) e o preview de QA.
const HOME_NOVA_ROUTES = ['/', '/preview/home/[key]'];

// The dynamic() and its loading fallback live in lazy-dashboard-layout so the same module can also expose
// the prefetch used by the buttons that route into the dashboard.

const MyApp = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const isDashboard = router.pathname.startsWith('/dashboard');
  // SSO handoff pages (/auth/*, SCRUM-1955): no tracker, no page_view, no UTM/clickId capture, no consent
  // banner — there is nothing to consent to. They are entry points reached by a full page load (native
  // client, Discord redirect), so there is never an already-loaded container to tear down. The Discord
  // callback's `createAccount`/trial events still reach GTM: `sendGTMEvent` queues them in `dataLayer`
  // and the container replays the array once it mounts on the page the callback router.replace()s to —
  // but NOT in a safe order: when the conversion is the first event in the queue (the SSO case) the
  // conversion tags can run before the base tags (Yandex `ym is not a function`, Meta CompleteRegistration
  // dropped). The fix lives in the container (bases on Initialization + sequencing, SCRUM-2437), which has
  // to be published before or together with any deploy that relies on this path.
  const isTrackingExempt = isTrackingExemptRoute(router.pathname);
  // The home nova (/, the /{pais}/{idioma} pairs and its QA preview) gets the thin cookie strip, always visible at the
  // bottom of the screen until the visitor answers. Every other page keeps the site banner. Same copy, buttons and flag.
  const isHomeNova = HOME_NOVA_ROUTES.includes(router.pathname);

  // `_document` only runs on the server, so a locale change through router.push
  // would otherwise leave <html lang>/<html dir> stale until a reload.
  useDocumentLanguage(pageProps.htmlLang);

  // Same reason for <html data-home-cta> (SCRUM-2631): the inline script in `_document` marks it on a full load only,
  // and a client-side visit to the home may set or clear the cookie (its `/_next/data` answer carries the Set-Cookie).
  useEffect(() => {
    syncHomeCtaAttribute();
  }, [router.asPath]);

  // Track UTM parameters on every page load
  useUtmTracker(!isTrackingExempt);
  useClickIdCapture(!isTrackingExempt);

  // Memoize initialValues to prevent unnecessary re-renders
  const featureToggleInitialValues = useMemo(
    () => ({
      gamersDay: pageProps.isEnabledGamersDay,
      blackFriday: pageProps.isEnableBlackFriday,
      christmas: pageProps.isEnableChristmas,
      bonusDays: pageProps.isEnableBonusDays,
      redirectTrial1Day: pageProps.isEnableRedirectTrial1Day,
      energyJoinupCampaign: pageProps.isEnergyJoinupCampaignActive,
      homeHeroV2: pageProps.isEnabledHomeHeroV2,
      homeSoftwareSectionV2: pageProps.isEnabledHomeSoftwareSectionV2,
    }),
    [pageProps.isEnabledGamersDay, pageProps.isEnableBlackFriday, pageProps.isEnableChristmas, pageProps.isEnableBonusDays, pageProps.isEnableRedirectTrial1Day, pageProps.isEnergyJoinupCampaignActive, pageProps.isEnabledHomeHeroV2, pageProps.isEnabledHomeSoftwareSectionV2]
  );

  // `GoogleTagManager` has no `strategy` prop — it renders two `next/script` elements (`_next-gtm-init`
  // and `_next-gtm`) with no strategy, so both default to `afterInteractive` and the container starts
  // downloading during hydration, i.e. inside the window Lighthouse charges against LCP. The only lever
  // left is *when the component mounts*, since `next/script` injects the tag from its own mount effect.
  //
  // Measured on the home page with the PageSpeed API, 8 mobile runs: the container is 499-684 KB, the
  // heaviest item on every page. The 7 runs where gtm.js finished before the observed paint simulated LCP
  // at 6,3-10,9 s and scored 55-67; the 1 run where it finished after simulated 2,9 s and scored 86 —
  // observed LCP was 823-2550 ms in all of them, so nothing here is about the page actually being slow,
  // only about which side of the paint the request lands on. Expected: +20 to +25 median mobile points.
  //
  // Nothing is lost by mounting later: `sendGTMEvent` pushes into `window.dataLayer` (creating it if
  // needed) and gtm.js replays the whole array on init, so the `page_view` below queues and is consumed
  // when the container boots. The consent-mode `<script>` rendered just before the container in the tree
  // below is untouched: it is plain HTML in the SSR output, so it executes while the parser reaches it —
  // long before this — and `gtag('consent','default',...)` keeps landing ahead of gtm.js, which is the
  // ordering consent mode requires.
  //
  // No consent gate (SCRUM-1954, decision of 20/10): the notice is informational, so the container mounts
  // on arrival with Consent Mode already `granted`, and the `page_view` goes out with it. The SCRUM-2324
  // gate was reverted by that decision. The load + idle gate below is a performance one, not consent's.
  // The name describes the gate (after load, then idle), not one script: it carried `<GoogleAnalytics>`
  // too until that tag turned out to be a duplicate of the container's own GA4 tag (see below).
  const [areDeferredScriptsMounted, setAreDeferredScriptsMounted] = useState(false);

  useEffect(() => runAfterLoadWhenIdle(() => setAreDeferredScriptsMounted(true)), []);

  // Read here, not at module scope, only so the value is a plain build-time constant at the point the
  // consent script below is assembled — Next inlines `process.env.NEXT_PUBLIC_*` textually wherever it
  // appears. Empty/undefined (a local build, a misconfigured env) emits no config at all.
  const googleAnalyticsId = process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS;
  // Interpolated into an inline <script> that runs on every page, so it goes in as a JSON string literal
  // (with `<` escaped, so not even `</script>` can end the tag): a raw quote in the env value would
  // otherwise break the consent defaults site-wide instead of failing one tag.
  const googleAnalyticsIdLiteral = googleAnalyticsId ? JSON.stringify(googleAnalyticsId).replace(/</g, '\\u003c') : '';

  useEffect(() => {
    if (isTrackingExempt) return;
    const props = {
      page_title: pageProps.slug || null,
    };
    sendGTMEvent({ event: 'page_view', ...props });
  }, [pageProps.slug, isTrackingExempt]);

  // This warm-up is a measured cost on every page of the site — 95 KB starting at 644 ms in the round-1
  // PageSpeed trace of /pt, i.e. inside the window Lighthouse charges against LCP — and it was removed
  // earlier in this branch for exactly that reason. It is back on purpose.
  //
  // What it hides is that /blog renders per request against WordPress with no cache and no timeout
  // (getServerSideProps + four wpFetch calls), so without a warm cache the first visitor pays the full cold
  // path, and a WordPress hiccup is served live as a 200 with zero posts. Removing the warm-up without
  // fixing that just moves the cost onto a real reader. The fix is a cache layer in front of the blog, which
  // is a separate piece of work with its own analysis — until it lands, this stays.
  //
  // What did change is *when* it fires: it used to run at module scope, during hydration. The purpose is to
  // have the cache warm before the first real reader arrives, and that survives a second of delay intact —
  // being charged against the LCP of every page of the site does not. Same gate as the Google tags below,
  // for the same reason. A visitor who leaves before load + idle no longer warms anything, which was
  // already true of one who left before hydration.
  useEffect(() => {
    if (process.env.NEXT_PUBLIC_ENV === 'development') return undefined;

    return runAfterLoadWhenIdle(() => {
      warmBlogCache();
    });
  }, []);

  // The country is read by the captcha bypass, the pricing list and the gateway lists — never by the
  // first paint. Fired during hydration it was a High-priority request inside the window Lighthouse
  // attributes to LCP; on idle it costs the score nothing. Resolving later is not a race:
  // `country-code-store` exists precisely to notify readers when this lands after their mount.
  useEffect(() => {
    if (sessionStorage.getItem(SESSION_STORAGE_COUNTRY_CODE)) return undefined;
    const baseURL = process.env.NEXT_PUBLIC_API_URL;

    return runWhenIdle(() => {
      fetch(`${baseURL}/user/getIp`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
        .then((res) => {
          if (!res.ok) {
            setCountryCode(CountryCodeEnum.UNITED_STATES);
            return null;
          }
          return res.json();
        })
        .then((data: { data: string } | null) => {
          if (data && data.data) {
            setCountryCode(data.data);
          }
        })
        .catch(() => setCountryCode(CountryCodeEnum.UNITED_STATES));
    });
  }, []);

  return (
    <Provider store={store}>
      {/* GoogleOAuthProvider used to sit here, which made @react-oauth/google inject 97 KB of
          accounts.google.com/gsi/client into every page of the site. It now travels with the things that
          can actually use it — see GoogleOAuthScope in components/modals/sso. */}
      <AuthProvider>
        <CieloProvider>
          <FeatureToggleProvider initialValues={featureToggleInitialValues}>
            {/* Parse-time inline script, deliberately outside the gate below. Consent Mode v2 `default` with
                every signal `granted` (SCRUM-1954: the cookie notice is informational — see
                `consentModeBootstrapScript` in `src/utils/consent.ts`), then the GA4 destination with `js` +
                `config`, because gtag.js replays `dataLayer`
                *in order* and an `event` that sits before the first `config` has no configured destination
                when its turn comes — it is dropped, silently. GA4 is loaded by the container, behind the
                load+idle gate, so its own `config` only lands after `load`, and every `window.gtag('event',
                …)` fired earlier would sit ahead of it. `pricing-list.tsx` fires `view_item_list` as soon as
                the plans respond, a few hundred ms in, i.e. always earlier.

                Measured against the real gtag.js in headless Chromium (21/09/2026, consent granted and
                consent denied, identical results). Event before config: the only hit sent is `page_view` —
                `view_item_list` never reaches /g/collect. With the two lines below, the hits are
                `view_item_list` *and* `page_view`. So this is not defensive ordering, it recovers an
                e-commerce event that is being lost today.

                `send_page_view: false` is load-bearing: the container's Google tag pushes its own `config`
                for the same measurement ID with `send_page_view` on, and in the same experiment two configs
                that both send a page view produced *two* `page_view` hits. With the flag, exactly one — the
                container's.

                Since the standalone gtag.js left (#1041), that is no longer enough on its own: the container's
                Google tag does not put the property in gtag's default group, so a direct event also needs
                `send_to` (`ga4EventTarget()`, src/utils/ga4-event-target.ts) or it has no destination at all.
                Measured 23/10/2026 on a production build with the real container: without `send_to`, no
                hit; with it, the event and the container's `page_view` (scripts/perf/verify-local-build.mjs).

                This costs no bytes and no gate: `gtag(...)` is a `dataLayer.push` of an arguments object.
                The request only happens when gtag.js loads, which stays deferred (verified: zero requests to
                googletagmanager.com/google-analytics.com before `load`). */}
            <script
              dangerouslySetInnerHTML={{
                __html: `${consentModeBootstrapScript()}
                ${
                  googleAnalyticsId
                    ? `gtag('js', new Date());
                gtag('config', ${googleAnalyticsIdLiteral}, { send_page_view: false });`
                    : ''
                }
              `,
              }}
            />
            {/* Mounted only after load + idle — see the areDeferredScriptsMounted comment above. Rendering
                it unconditionally is the regression this guards against, and it costs ~20-25 mobile points.
                Off on /auth/* (SCRUM-1955) — including OpenAiAdsPixel: the Discord callback's
                `trial_started` therefore goes out through the Conversions API only, on purpose
                (see docs/openai-ads-conversions.md). */}
            {!isTrackingExempt && (
              <>
                {areDeferredScriptsMounted && <GoogleTagManager gtmId={process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER!} />}
                {/* No `<GoogleAnalytics>` of our own: the container `GTM-K78ZZ2B7` fires a Google tag for
                    `G-3QY3D3HM2N` with `send_page_view` on every page (`gtm.js` trigger, read from the
                    published container on 22/10/2026). Rendering ours as well downloaded, parsed and ran the
                    same `gtag/js` twice — 197 KB and ~100 ms of CPU per page, the only item of
                    third-party-blockers.md that removes TBT instead of moving it (blocker 4). The early `config`
                    in the consent script above stays: it is what keeps events queued before the container
                    from being dropped. */}
                <YandexMetrica />
                <PostHogAnalytics />
                <OpenAiAdsPixel />
                <KangWidget />
              </>
            )}
            <TrialSignUpProvider>
              <div className={bai_jamjuree.className}>
                {/* Before the page on purpose: the banner is `fixed`, so this changes nothing visually, but it makes
                    it the first thing Tab reaches instead of the last — after a whole page hidden behind it. */}
                {!isTrackingExempt && <CookieConsent variant={isHomeNova ? 'compact' : 'default'} />}
                {isDashboard ? (
                  <LazyDashboardLayout>
                    <Component {...pageProps} />
                  </LazyDashboardLayout>
                ) : (
                  <Component {...pageProps} />
                )}
              </div>
            </TrialSignUpProvider>
            <ToastHost />
          </FeatureToggleProvider>
        </CieloProvider>
      </AuthProvider>
    </Provider>
  );
};

export default appWithTranslation(MyApp);
